import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Users, Trophy, Target, TrendingUp, UserCog, Building2, Sparkles, Key,
  BarChart3, ClipboardList, Network, KeyRound, Pencil, Settings, Trash2,
  UserPlus, BookOpen, CheckCircle2, X, ChevronRight
} from 'lucide-react';

export default function AdminPanel() {
  const navigate = useNavigate();
  const { axiosAuth, user: authUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  // Edit user dialog
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', org_id: '', vertical_id: '', team_id: '' });

  // Reset password dialog
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Org data for dropdowns
  const [orgs, setOrgs] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => { fetchData(); }, [axiosAuth]);

  useEffect(() => {
    if (authUser?.role === 'org_admin' && !localStorage.getItem('org_admin_guide_dismissed')) {
      setShowGuide(true);
    }
  }, [authUser]);

  const fetchData = async () => {
    try {
      const api = axiosAuth();
      const [dashRes, usersRes, orgsRes, vertsRes, teamsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users'),
        api.get('/admin/orgs').catch(() => ({ data: { orgs: [] } })),
        api.get('/admin/verticals').catch(() => ({ data: { verticals: [] } })),
        api.get('/admin/teams').catch(() => ({ data: { teams: [] } })),
      ]);
      setDashboard(dashRes.data);
      setUsers(usersRes.data.users);
      setOrgs(orgsRes.data.orgs || []);
      setVerticals(vertsRes.data.verticals || []);
      setTeams(teamsRes.data.teams || []);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
      else toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || '',
      role: u.role || 'user',
      org_id: u.org_id || '',
      vertical_id: u.vertical_id || '',
      team_id: u.team_id || '',
    });
  };

  const saveUserUpdate = async () => {
    if (!editUser) return;
    try {
      await axiosAuth().put(`/admin/users/${editUser.id}/update`, editForm);
      toast.success('User updated');
      setEditUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const resetPassword = async () => {
    if (!resetUser) return;
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setResetLoading(true);
    try {
      await axiosAuth().put(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setResetLoading(false); }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await axiosAuth().put(`/admin/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch { toast.error('Failed to update role'); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.name} (${user.email})? This will also delete all their simulation data. This cannot be undone.`)) return;
    try {
      await axiosAuth().delete(`/admin/users/${user.id}`);
      toast.success(`${user.name} deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // All hooks must be before early returns
  const orgMap = useMemo(() => Object.fromEntries(orgs.map(o => [o.id, o.name])), [orgs]);
  const vertMap = useMemo(() => Object.fromEntries(verticals.map(v => [v.id, v.name])), [verticals]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t.name])), [teams]);
  const getOrgName = useCallback((id) => orgMap[id] || '', [orgMap]);
  const getVertName = useCallback((id) => vertMap[id] || '', [vertMap]);
  const getTeamName = useCallback((id) => teamMap[id] || '', [teamMap]);
  const editVerticals = useMemo(() => verticals.filter(v => v.org_id === editForm.org_id), [verticals, editForm.org_id]);
  const editTeams = useMemo(() => teams.filter(t => t.vertical_id === editForm.vertical_id), [teams, editForm.vertical_id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading admin panel...</p></div>
    </div>
  );

  const stats = [
    { label: 'Users', value: dashboard?.total_users || 0, icon: Users, color: 'text-brand' },
    { label: 'Simulations', value: dashboard?.total_simulations || 0, icon: Target, color: 'text-info' },
    { label: 'Completed', value: dashboard?.completed_simulations || 0, icon: Trophy, color: 'text-success' },
    { label: 'Org Avg', value: dashboard?.avg_org_score ? `${dashboard.avg_org_score}/10` : '--', icon: TrendingUp, color: 'text-warning' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel-page">
      <Navbar showBack backTo="/" backLabel="Admin Panel" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Organization overview and user management</p>
        </div>

        {showGuide && (
          <div data-testid="org-admin-guide" className="heritage-card p-5 sm:p-6 mb-8 border-l-4 border-brand relative">
            <button data-testid="dismiss-guide" onClick={() => { setShowGuide(false); localStorage.setItem('org_admin_guide_dismissed', '1'); }}
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted transition-colors" aria-label="Dismiss guide">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
            <h2 className="font-heading text-base sm:text-lg font-bold mb-1 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-brand" /> Welcome, Org Admin
            </h2>
            <p className="font-body text-xs text-muted-foreground mb-4">Here's how to get your team up and running in 3 steps.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={() => { document.getElementById('user-mgmt')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="text-left bg-brand/5 rounded-xl p-4 border border-brand/10 hover:border-brand/30 transition-colors group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center text-xs font-bold text-brand">1</span>
                  <span className="font-heading text-sm font-bold">Add Users</span>
                </div>
                <p className="font-body text-[11px] text-muted-foreground leading-relaxed">Scroll down to User Management and click <strong>+ Add User</strong> to create accounts for your team members.</p>
                <span className="flex items-center gap-1 text-[11px] text-brand font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-3 h-3" /> Go to User Management</span>
              </button>
              <button onClick={() => navigate('/admin/scenarios')}
                className="text-left bg-info/5 rounded-xl p-4 border border-info/10 hover:border-info/30 transition-colors group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-info/15 flex items-center justify-center text-xs font-bold text-info">2</span>
                  <span className="font-heading text-sm font-bold">Browse Scenarios</span>
                </div>
                <p className="font-body text-[11px] text-muted-foreground leading-relaxed">Explore 15+ built-in scenarios. Pick the ones that match your team's development goals.</p>
                <span className="flex items-center gap-1 text-[11px] text-info font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-3 h-3" /> Open Scenario Builder</span>
              </button>
              <button onClick={() => { const el = document.querySelector('[data-testid="assign-btn"]'); if (el) el.click(); else navigate('/admin'); }}
                className="text-left bg-success/5 rounded-xl p-4 border border-success/10 hover:border-success/30 transition-colors group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-success/15 flex items-center justify-center text-xs font-bold text-success">3</span>
                  <span className="font-heading text-sm font-bold">Assign Simulations</span>
                </div>
                <p className="font-body text-[11px] text-muted-foreground leading-relaxed">Click <strong>Assign</strong> above to assign scenarios to specific users with due dates.</p>
                <span className="flex items-center gap-1 text-[11px] text-success font-medium mt-2 opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-3 h-3" /> Open Assignments</span>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          {stats.map((s, i) => (
            <div key={i} className="heritage-card p-4" data-testid={`admin-stat-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`w-4 h-4 ${s.color}`} />
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{s.label}</span>
              </div>
              <p className="font-heading text-xl sm:text-2xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Admin Quick Actions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="heritage-card p-4 sm:p-5 flex items-center justify-between" data-testid="scenario-builder-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center"><Sparkles className="w-5 h-5 text-accent" /></div>
              <div><p className="font-heading text-sm font-bold">Scenario Builder</p><p className="font-body text-xs text-muted-foreground">Create custom scenarios from templates</p></div>
            </div>
            <Button data-testid="open-scenario-builder-btn" onClick={() => navigate('/admin/scenarios')}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90"><Sparkles className="w-3.5 h-3.5" /> Builder</Button>
          </div>
          {authUser?.role === 'super_admin' && (
          <div className="heritage-card p-4 sm:p-5 flex items-center justify-between" data-testid="org-management-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center"><Network className="w-5 h-5 text-brand" /></div>
              <div><p className="font-heading text-sm font-bold">Organization Hierarchy</p><p className="font-body text-xs text-muted-foreground">Manage orgs, verticals, teams & users</p></div>
            </div>
            <Button data-testid="open-org-management-btn" onClick={() => navigate('/admin/org')}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90"><Network className="w-3.5 h-3.5" /> Manage</Button>
          </div>
          )}
          <div className="heritage-card p-4 sm:p-5 flex items-center justify-between" data-testid="reports-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center"><BarChart3 className="w-5 h-5 text-success" /></div>
              <div><p className="font-heading text-sm font-bold">Performance Reports</p><p className="font-body text-xs text-muted-foreground">Individual, team & org level reports</p></div>
            </div>
            <Button data-testid="open-reports-btn" onClick={() => navigate('/admin/reports')}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90"><BarChart3 className="w-3.5 h-3.5" /> Reports</Button>
          </div>
          <div className="heritage-card p-4 sm:p-5 flex items-center justify-between" data-testid="assignments-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center"><ClipboardList className="w-5 h-5 text-warning" /></div>
              <div><p className="font-heading text-sm font-bold">Simulation Assignments</p><p className="font-body text-xs text-muted-foreground">Assign scenarios to users with due dates</p></div>
            </div>
            <Button data-testid="assign-btn" onClick={() => navigate('/admin/assignments')}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90"><ClipboardList className="w-3.5 h-3.5" /> Assign</Button>
          </div>
          {authUser?.role === 'super_admin' && (<>
          <div className="heritage-card p-4 sm:p-5 flex items-center justify-between" data-testid="api-integration-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-info/10 rounded-lg flex items-center justify-center"><Key className="w-5 h-5 text-info" /></div>
              <div><p className="font-heading text-sm font-bold">API Integration</p><p className="font-body text-xs text-muted-foreground">REST API & webhooks for LMS tools</p></div>
            </div>
            <Button data-testid="open-api-integration-btn" onClick={() => navigate('/admin/api')}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90"><Key className="w-3.5 h-3.5" /> APIs</Button>
          </div>
          <div className="heritage-card p-4 sm:p-5 flex items-center justify-between" data-testid="report-builder-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center"><Settings className="w-5 h-5 text-purple-500" /></div>
              <div><p className="font-heading text-sm font-bold">Report Builder</p><p className="font-body text-xs text-muted-foreground">Configure scoring weights & competencies</p></div>
            </div>
            <Button data-testid="open-report-builder-btn" onClick={() => navigate('/admin/report-builder')}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90"><Settings className="w-3.5 h-3.5" /> Configure</Button>
          </div>
          </>)}
        </div>

        {/* Teams */}
        {dashboard?.teams?.length > 0 && (
          <div className="mb-6">
            <h2 className="font-heading text-base sm:text-lg font-bold mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /> Team Breakdown</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {dashboard.teams.map((team, i) => (
                <div key={i} className="heritage-card p-4" data-testid={`team-card-${i}`}>
                  <p className="font-heading text-sm font-bold mb-1.5">{team.team || 'Unassigned'}</p>
                  <div className="flex items-center gap-4 font-body text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {team.members}</span>
                    <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> {team.completed} done</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Management */}
        <div id="user-mgmt">
          <h2 className="font-heading text-base sm:text-lg font-bold mb-3 flex items-center gap-2"><UserCog className="w-4 h-4 text-muted-foreground" /> User Management</h2>
          <div className="space-y-2" data-testid="admin-user-list">
            {users.map((u, i) => (
              <div key={u.id} className="heritage-card p-3 sm:p-4 flex items-center gap-3 sm:gap-4" data-testid={`admin-user-${i}`}>
                <div className="w-9 h-9 bg-brand text-[#333333] rounded-full flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
                  {u.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-bold truncate">{u.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="font-body text-[11px] text-muted-foreground">{u.email}</span>
                    {u.org_id && getOrgName(u.org_id) && <Badge className="bg-brand/10 text-brand border-none font-body text-[9px] px-1 py-0">{getOrgName(u.org_id)}</Badge>}
                    {u.team_id && getTeamName(u.team_id) && <Badge className="bg-success/10 text-success border-none font-body text-[9px] px-1 py-0">{getTeamName(u.team_id)}</Badge>}
                    <Badge className={`${['super_admin','org_admin'].includes(u.role) ? 'bg-accent/10 text-accent' : u.role === 'manager' ? 'bg-brand/10 text-brand' : 'bg-muted text-muted-foreground'} border-none font-body text-[10px] px-1.5 py-0`}>{u.role?.replace('_',' ')}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="font-heading text-sm font-bold">{u.avg_score}/10</p>
                    <p className="font-body text-[10px] text-muted-foreground">{u.completed_count} sims</p>
                  </div>
                  {['super_admin', 'org_admin'].includes(authUser?.role) && (
                  <>
                  <Select value={u.role} onValueChange={(val) => updateRole(u.id, val)}>
                    <SelectTrigger data-testid={`role-select-${i}`} className="w-28 border border-border h-7 font-body text-[11px] bg-surface">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border border-border shadow-modal bg-surface">
                      <SelectItem value="user" className="font-body text-xs">User</SelectItem>
                      <SelectItem value="manager" className="font-body text-xs">Manager</SelectItem>
                      {authUser?.role === 'super_admin' && (
                      <>
                        <SelectItem value="org_admin" className="font-body text-xs">Org Admin</SelectItem>
                        <SelectItem value="super_admin" className="font-body text-xs">Super Admin</SelectItem>
                      </>
                      )}
                    </SelectContent>
                  </Select>
                  <button data-testid={`edit-user-${i}`} onClick={() => openEditUser(u)}
                    className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-muted transition-colors" title="Edit user">
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                  </button>
                  <button data-testid={`reset-password-${i}`} onClick={() => { setResetUser(u); setNewPassword(''); setConfirmPassword(''); }}
                    className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-warning/10 hover:border-warning/30 transition-colors" title="Reset password">
                    <KeyRound className="w-3 h-3 text-muted-foreground" />
                  </button>
                  <button data-testid={`delete-user-${i}`} onClick={() => deleteUser(u)}
                    className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-red-500/10 hover:border-red-500/30 transition-colors" title="Delete user">
                    <Trash2 className="w-3 h-3 text-muted-foreground" />
                  </button>
                  </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Edit User</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Update details and group assignment for {editUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Name</label>
              <input data-testid="edit-user-name" type="text" value={editForm.name}
                onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                className="heritage-input w-full text-sm" />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Role</label>
              <Select value={editForm.role} onValueChange={val => setEditForm(p => ({ ...p, role: val }))}>
                <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue /></SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface">
                  <SelectItem value="user" className="font-body text-sm">User</SelectItem>
                  <SelectItem value="manager" className="font-body text-sm">Manager</SelectItem>
                  <SelectItem value="org_admin" className="font-body text-sm">Org Admin</SelectItem>
                  <SelectItem value="super_admin" className="font-body text-sm">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Organization</label>
              <Select value={editForm.org_id || '_none'} onValueChange={val => setEditForm(p => ({ ...p, org_id: val === '_none' ? '' : val, vertical_id: '', team_id: '' }))}>
                <SelectTrigger data-testid="edit-user-org" className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface">
                  <SelectItem value="_none" className="font-body text-sm">None</SelectItem>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id} className="font-body text-sm">{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editForm.org_id && (
              <div>
                <label className="font-body text-xs text-muted-foreground mb-1 block">Vertical</label>
                <Select value={editForm.vertical_id || '_none'} onValueChange={val => setEditForm(p => ({ ...p, vertical_id: val === '_none' ? '' : val, team_id: '' }))}>
                  <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="border border-border shadow-modal bg-surface">
                    <SelectItem value="_none" className="font-body text-sm">None</SelectItem>
                    {editVerticals.map(v => <SelectItem key={v.id} value={v.id} className="font-body text-sm">{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editForm.vertical_id && (
              <div>
                <label className="font-body text-xs text-muted-foreground mb-1 block">Team</label>
                <Select value={editForm.team_id || '_none'} onValueChange={val => setEditForm(p => ({ ...p, team_id: val === '_none' ? '' : val }))}>
                  <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="border border-border shadow-modal bg-surface">
                    <SelectItem value="_none" className="font-body text-sm">None</SelectItem>
                    {editTeams.map(t => <SelectItem key={t.id} value={t.id} className="font-body text-sm">{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setEditUser(null)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="edit-user-save-btn" onClick={saveUserUpdate} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetUser} onOpenChange={(open) => !open && setResetUser(null)}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-sm" data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-warning" /> Reset Password
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">
              Set a new password for {resetUser?.name} ({resetUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">New Password</label>
              <input data-testid="admin-new-password" type="password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="heritage-input w-full text-sm" placeholder="Min 6 characters" />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Confirm Password</label>
              <input data-testid="admin-confirm-password" type="password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="heritage-input w-full text-sm" placeholder="Confirm password" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setResetUser(null)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="admin-reset-submit-btn" onClick={resetPassword} disabled={resetLoading}
                className="flex-1 h-9 bg-warning text-white text-sm rounded-lg hover:opacity-90">
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
